import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { PageMetaOverride } from "@/core/navigation/breadcrumb-trail";

const EMPTY_META: PageMetaOverride = {};

interface PageMetaContextValue {
  meta: PageMetaOverride;
  setMeta: (meta: PageMetaOverride) => void;
}

const PageMetaContext = createContext<PageMetaContextValue | null>(null);

export function PageMetaProvider({ children }: { children: ReactNode }) {
  const [meta, setMetaState] = useState<PageMetaOverride>(EMPTY_META);
  const setMeta = useCallback((next: PageMetaOverride) => {
    setMetaState(next);
  }, []);

  const value = useMemo(() => ({ meta, setMeta }), [meta, setMeta]);

  return <PageMetaContext.Provider value={value}>{children}</PageMetaContext.Provider>;
}

export function usePageMetaContext(): PageMetaContextValue {
  const ctx = useContext(PageMetaContext);
  if (!ctx) {
    throw new Error("usePageMetaContext must be used within PageMetaProvider");
  }
  return ctx;
}

/** Enregistre titre / libellé fil d'Ariane pour les pages détail (reset au démontage). */
export function usePageMeta(meta: PageMetaOverride | null | undefined): void {
  const { setMeta } = usePageMetaContext();

  useEffect(() => {
    if (!meta?.pageTitle && !meta?.breadcrumbLabel) {
      setMeta(EMPTY_META);
      return;
    }
    setMeta({
      pageTitle: meta.pageTitle,
      breadcrumbLabel: meta.breadcrumbLabel,
    });
    return () => setMeta(EMPTY_META);
  }, [meta?.pageTitle, meta?.breadcrumbLabel, setMeta]);
}
